"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  Checkbox,
  CopyButton,
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildCerParagraph,
  CER_CLAIM_FRAMES,
  CER_CLOSING_FRAMES,
  CER_CONNECTORS,
  CER_EVIDENCE_LEADS,
  CER_QUESTION_FRAMES,
  CER_REASONING_FRAMES,
  CER_VERSIONS,
  type CerCheck,
  type CerField,
  type CerInput,
  type CerSentence,
  type CheckStatus,
  cerChecks,
  missingCerFields,
} from "@/lib/tool-kit/essay/cer";
import { joinSeries } from "@/lib/tool-kit/essay/sentence";

/** English strings. A translated content file overrides these through `ui`. */
export const CER_PARAGRAPH_GENERATOR_STRINGS = {
  questionLabel: "Investigation question (optional)",
  questionPlaceholder:
    "e.g. How does light intensity affect the rate of photosynthesis in pondweed?",
  questionHint:
    "The question your claim answers. It also lets the checklist test that it does.",
  claimLabel: "Claim",
  claimPlaceholder:
    "e.g. the rate of photosynthesis increases as light intensity increases",
  claimHint: "One sentence that answers the question. No “I think”.",
  evidenceLabel: "Evidence",
  evidencePlaceholder:
    "e.g. At 10 cm from the lamp, the pondweed released an average of 28 bubbles per minute.\nAt 30 cm, the average fell to 11 bubbles per minute.",
  evidenceHint:
    "Data or observations from your investigation — one per line, with units.",
  reasoningLabel: "Reasoning",
  reasoningPlaceholder:
    "e.g. light supplies the energy that drives photosynthesis, so more light lets the plant release oxygen faster",
  reasoningHint:
    "The scientific principle that explains why your evidence supports your claim.",
  tryExample: "Try an example",
  clear: "Clear",
  fieldNames: {
    question: "a question",
    claim: "a claim",
    evidence: "evidence",
    reasoning: "reasoning",
  },
  and: "and",
  missingNotice: "Add {fields} to build the paragraph.",
  resultsHeading: "Your CER paragraph",
  resultsIntro:
    "Three wordings built from your own claim, data, and reasoning — honest templates, no AI. Choose one and smooth it into your own voice.",
  versionLabel: "Version {n}",
  showLabels: "Show the C, E, and R in each version",
  copy: "Copy",
  copied: "Copied!",
  partLabels: {
    question: "Question",
    claim: "Claim",
    evidence: "Evidence",
    reasoning: "Reasoning",
  },
  peelTipBefore: "Writing about a text rather than an experiment? The ",
  peelTipLink: "PEEL Paragraph Planner",
  peelTipAfter: " suits literary and historical evidence better.",
  checklistHeading: "What each part needs",
  checklistIntro:
    "These checks read words and numbers, not meaning — a tick says a feature is present, not that the science is right.",
  checkGroups: {
    claim: "Claim",
    evidence: "Evidence",
    reasoning: "Reasoning",
  },
  statusLabels: {
    ok: "Looks good",
    check: "Worth a look",
    manual: "Check yourself",
  },
  checks: {
    claimOneSentence: "States the claim in one sentence",
    claimDirect: "States it directly, without “I think” or “I believe”",
    answersQuestion: "Answers the question that was asked",
    noProve: "Says the data support the claim, not that they prove it",
    evidenceData: "Gives specific data — measurements or observations",
    evidenceUnits: "Includes a unit with each measurement",
    evidenceSeveral: "Uses more than one piece of evidence",
    evidenceRelevant: "Every piece of evidence relates directly to the claim",
    reasoningPrinciple:
      "Names the scientific principle, law, or concept that links evidence to claim",
    reasoningNotData: "Explains the science instead of repeating the data",
    reasoningLength: "Develops the reasoning in a full sentence or two",
  },
  checkTips: {
    claimOneSentence:
      "Your claim runs past one sentence. Keep the answer itself to one and move the rest into evidence or reasoning.",
    claimDirect:
      "Drop “I think” or “I believe” — the evidence, not your opinion, is what makes the claim convincing.",
    answersQuestion:
      "The claim shares no key words with the question. Make sure it answers what was asked.",
    noProve:
      "Swap “prove” for “support” or “show”: one investigation rarely proves anything in science.",
    evidenceData:
      "No numbers found. Observations count, but give measured values wherever you have them.",
    evidenceUnits:
      "Add units (cm, g, °C, s, mL…) so each number means something.",
    evidenceSeveral:
      "One data point is thin. Add another trial, condition, or observation if you have one.",
    evidenceRelevant:
      "Cut any data point that does not bear on the claim — extra numbers dilute the paragraph.",
    reasoningPrinciple:
      "Name the rule itself, e.g. “light energy drives photosynthesis” or “particles move faster when heated.”",
    reasoningNotData:
      "Your reasoning repeats numbers from the evidence. Use it to explain why the pattern happens.",
    reasoningLength:
      "Reasoning is usually the longest part: explain why the evidence supports the claim, not just that it does.",
  },
  questionFrames: CER_QUESTION_FRAMES,
  claimFrames: CER_CLAIM_FRAMES,
  evidenceLeads: CER_EVIDENCE_LEADS,
  connectors: CER_CONNECTORS,
  reasoningFrames: CER_REASONING_FRAMES,
  closingFrames: CER_CLOSING_FRAMES,
  example: {
    question:
      "How does light intensity affect the rate of photosynthesis in pondweed?",
    claim: "the rate of photosynthesis increases as light intensity increases",
    evidence:
      "At 10 cm from the lamp, the pondweed released an average of 28 oxygen bubbles per minute over three trials.\nAt 30 cm, the average fell to 11 bubbles per minute.\nAt 50 cm, it fell to 4 bubbles per minute.",
    reasoning:
      "light supplies the energy for the light-dependent reactions of photosynthesis, which split water and release oxygen, so a brighter light lets the plant produce oxygen faster until another factor, such as carbon dioxide, becomes limiting",
  },
} as const;

type Strings = typeof CER_PARAGRAPH_GENERATOR_STRINGS;

const EMPTY_INPUT: CerInput = {
  question: "",
  claim: "",
  evidence: "",
  reasoning: "",
};

const BADGE =
  "inline-flex h-fit w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";

const STATUS_ICON: Record<CheckStatus, string> = {
  ok: "✓",
  check: "!",
  manual: "○",
};

const STATUS_TONE: Record<CheckStatus, string> = {
  ok: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  check: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  manual: "bg-muted text-muted-foreground",
};

const CHECK_GROUPS = ["claim", "evidence", "reasoning"] as const;

/** Checks no surface test can settle: their tip is the guidance, so it always shows. */
const ALWAYS_MANUAL: ReadonlySet<string> = new Set([
  "evidenceRelevant",
  "reasoningPrinciple",
]);

function showsTip(check: CerCheck): boolean {
  return check.status === "check" || ALWAYS_MANUAL.has(check.id);
}

/** A part the reader has not filled yet is a requirement to read, not a failure. */
function liveChecks(input: CerInput, locale: string): CerCheck[] {
  const empty = {
    claim: input.claim.trim() === "",
    evidence: input.evidence.trim() === "",
    reasoning: input.reasoning.trim() === "",
  };
  return cerChecks(input, locale).map((check) =>
    empty[check.part] ? { ...check, status: "manual" as const } : check
  );
}

function paragraphText(sentences: readonly CerSentence[]): string {
  return sentences.map((sentence) => sentence.text).join(" ");
}

function Paragraph({
  sentences,
  showLabels,
  strings,
}: {
  sentences: readonly CerSentence[];
  showLabels: boolean;
  strings: Strings;
}) {
  if (!showLabels) {
    return (
      <p className="text-foreground text-sm leading-relaxed">
        {paragraphText(sentences)}
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {sentences.map((sentence, index) => (
        // Sentences are positional: two evidence lines can share a part.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional sentences
        <li className="grid gap-1 sm:grid-cols-[7rem_1fr] sm:gap-3" key={index}>
          <span className={BADGE}>{strings.partLabels[sentence.part]}</span>
          <span className="text-foreground text-sm leading-relaxed">
            {sentence.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Checklist({
  checks,
  strings,
}: {
  checks: readonly CerCheck[];
  strings: Strings;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {CHECK_GROUPS.map((group) => (
        <div className={`${CARD} space-y-3`} key={group}>
          <h3 className="font-semibold text-foreground text-sm">
            {strings.checkGroups[group]}
          </h3>
          <ul className="space-y-3">
            {checks
              .filter((check) => check.part === group)
              .map((check) => (
                <li className="flex gap-2.5" key={check.id}>
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full font-semibold text-xs ${STATUS_TONE[check.status]}`}
                  >
                    {STATUS_ICON[check.status]}
                  </span>
                  <span className="space-y-1">
                    <span className="block text-foreground text-sm">
                      {strings.checks[check.id]}
                    </span>
                    <span className="block text-muted-foreground text-xs">
                      {strings.statusLabels[check.status]}
                      {showsTip(check)
                        ? ` — ${strings.checkTips[check.id]}`
                        : null}
                    </span>
                  </span>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function CerParagraphGeneratorWidget() {
  const strings = useToolUi(CER_PARAGRAPH_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [input, setInput] = useState<CerInput>(EMPTY_INPUT);
  const [showLabels, setShowLabels] = useState(true);

  const missing = missingCerFields(input);
  const frames = {
    questionFrames: strings.questionFrames,
    claimFrames: strings.claimFrames,
    evidenceLeads: strings.evidenceLeads,
    connectors: strings.connectors,
    reasoningFrames: strings.reasoningFrames,
    closingFrames: strings.closingFrames,
  };
  const versions =
    missing.length === 0
      ? CER_VERSIONS.map((version) => buildCerParagraph(input, version, frames))
      : [];

  function update(field: CerField, value: string) {
    setInput((previous) => ({ ...previous, [field]: value }));
  }

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <TextField
          hint={strings.questionHint}
          id="cer-question"
          label={strings.questionLabel}
          onChange={(value) => update("question", value)}
          placeholder={strings.questionPlaceholder}
          value={input.question}
        />
        <TextField
          hint={strings.claimHint}
          id="cer-claim"
          label={strings.claimLabel}
          onChange={(value) => update("claim", value)}
          placeholder={strings.claimPlaceholder}
          value={input.claim}
        />
        <TextAreaField
          hint={strings.evidenceHint}
          id="cer-evidence"
          label={strings.evidenceLabel}
          onChange={(value) => update("evidence", value)}
          placeholder={strings.evidencePlaceholder}
          prose
          rows={4}
          value={input.evidence}
        />
        <TextAreaField
          hint={strings.reasoningHint}
          id="cer-reasoning"
          label={strings.reasoningLabel}
          onChange={(value) => update("reasoning", value)}
          placeholder={strings.reasoningPlaceholder}
          prose
          rows={3}
          value={input.reasoning}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput({ ...strings.example })}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_INPUT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {versions.length === 0 ? (
          <Notice>
            {t(strings.missingNotice, {
              fields: joinSeries(
                missing.map((field) => strings.fieldNames[field]),
                strings.and
              ),
            })}
          </Notice>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.resultsIntro}
            </p>
            <Checkbox
              checked={showLabels}
              label={strings.showLabels}
              onChange={setShowLabels}
            />
            {versions.map((sentences, index) => (
              <div
                className={`${RESULT_CARD} space-y-3`}
                key={CER_VERSIONS[index]}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={WIDGET_H3}>
                    {t(strings.versionLabel, { n: fmt(index + 1) })}
                  </h3>
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    small
                    text={paragraphText(sentences)}
                  />
                </div>
                <Paragraph
                  sentences={sentences}
                  showLabels={showLabels}
                  strings={strings}
                />
              </div>
            ))}
          </>
        )}
        <p className={HINT}>
          {strings.peelTipBefore}
          <Link
            className="underline underline-offset-4 hover:text-foreground"
            href="/tools/peel-paragraph-generator"
          >
            {strings.peelTipLink}
          </Link>
          {strings.peelTipAfter}
        </p>
      </section>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.checklistHeading}</h2>
        <p className="text-muted-foreground text-sm">
          {strings.checklistIntro}
        </p>
        <Checklist checks={liveChecks(input, locale)} strings={strings} />
      </section>
    </>
  );
}
